import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';

interface AlertWatch {
  siteIds: string[];
  resourceIds: string[];
}

const EMPTY: AlertWatch = { siteIds: [], resourceIds: [] };

/**
 * Per-user watch list, editable inline from a resource/site page (this hook)
 * as well as from the full Settings > Alert sounds picker — both read and
 * write the same `alertWatch` field, so either one always reflects the other.
 */
export function useAlertWatch() {
  const [watch, setWatch] = useState<AlertWatch | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const me = await api.get<{ alertWatch?: AlertWatch }>('/api/auth/me');
      setWatch(me.alertWatch ?? EMPTY);
    } catch {
      setWatch(EMPTY);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = useCallback(
    async (scope: 'site' | 'resource', id: string) => {
      const current = watch ?? EMPTY;
      const key = scope === 'site' ? 'siteIds' : 'resourceIds';
      const list = current[key];
      const nextList = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      const updated: AlertWatch = { ...current, [key]: nextList };

      setWatch(updated);
      setBusy(true);
      try {
        await api.put('/api/me/alert-watch', updated);
      } catch (err) {
        setWatch(current); // revert the optimistic update
        throw err;
      } finally {
        setBusy(false);
      }
    },
    [watch],
  );

  return {
    loading: watch === null,
    busy,
    isWatchingSite: (id: string) => (watch ?? EMPTY).siteIds.includes(id),
    isWatchingResource: (id: string) => (watch ?? EMPTY).resourceIds.includes(id),
    toggleSite: (id: string) => toggle('site', id),
    toggleResource: (id: string) => toggle('resource', id),
  };
}
